/**
 * Boot: restore the session, then start the workout UI (public/app.js) with a
 * SyncStore behind it. Also owns the sign-in screen, the account sheet and
 * the "update available" banner.
 */
import type { ApiUser } from "@wreps/shared";
import { Auth } from "./auth.js";
import { renderGoogleButton } from "./google.js";
import { type AppData, type SyncStatus, SyncStore } from "./store.js";

interface WrepsAppGlobal {
  start(): Promise<void>;
  applyRemote(data: AppData): void;
  render(): void;
}

declare global {
  interface Window {
    WrepsApp: WrepsAppGlobal;
    WrepsStore: SyncStore;
    WrepsUI: {
      accountInitial(): string;
      afterRender(): void;
      openAccount(): void;
    };
    // Defined by public/app.js
    showToast(msg: string): void;
    showConfirm(message: string, onConfirm: () => void): void;
  }
}

const auth = new Auth();
let store: SyncStore | null = null;
let currentUser: ApiUser | null = null;

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

const STATUS_TEXT: Record<SyncStatus, string> = {
  synced: "All changes synced",
  pending: "Changes waiting to sync",
  syncing: "Syncing…",
  offline: "Offline: changes are saved on this device and will sync later",
  error: "Sync problem: retrying",
};

function syncLine(): string {
  if (!store) return "";
  const s = store.getStatus();
  const n = store.pendingCount();
  if ((s === "pending" || s === "offline" || s === "error") && n > 0) {
    return `${STATUS_TEXT[s]} (${n} unsynced ${n === 1 ? "change" : "changes"})`;
  }
  return STATUS_TEXT[s];
}

function paintSyncStatus(): void {
  const dot = document.getElementById("sync-dot");
  if (dot && store) {
    dot.dataset.status = store.getStatus();
    dot.title = syncLine();
    dot.setAttribute("aria-label", syncLine());
  }
  const line = document.getElementById("account-sync");
  if (line) line.textContent = syncLine();
}

// ---------- hooks used by public/app.js ----------

window.WrepsUI = {
  accountInitial: () => (currentUser?.name || currentUser?.email || "?").trim().charAt(0) || "?",
  afterRender: paintSyncStatus,
  openAccount,
};

// ---------- account sheet ----------

function openAccount(): void {
  $("account-who").textContent = currentUser?.email ?? currentUser?.name ?? "";
  paintSyncStatus();
  $("account-sheet").classList.add("show");
}

function closeAccount(): void {
  $("account-sheet").classList.remove("show");
}

async function signOut(): Promise<void> {
  if (store?.hasPending()) await store.syncNow();
  const doIt = async () => {
    closeAccount();
    await auth.signOut();
    store?.clearLocal();
    window.google?.accounts.id.disableAutoSelect();
    location.reload();
  };
  if (store?.hasPending()) {
    window.showConfirm(
      `${store.pendingCount()} change(s) haven't synced yet and will be lost. Sign out anyway?`,
      () => void doIt(),
    );
  } else {
    await doIt();
  }
}

function deleteAccount(): void {
  window.showConfirm(
    "Permanently delete your Wreps account and every workout in it? This can't be undone. (Export first if you want a copy.)",
    async () => {
      try {
        await auth.deleteAccount();
        store?.clearLocal();
        location.reload();
      } catch {
        window.showToast("Couldn't delete the account. Check your connection and try again.");
      }
    },
  );
}

$("account-close").addEventListener("click", closeAccount);
$("account-sheet").addEventListener("click", (e) => {
  if (e.target === e.currentTarget) closeAccount();
});
$("account-signout").addEventListener("click", () => void signOut());
$("account-delete").addEventListener("click", () => {
  closeAccount();
  deleteAccount();
});
$("account-sync-now").addEventListener("click", async () => {
  await store?.syncNow();
  paintSyncStatus();
});

// ---------- sign-in screen ----------

function showSignIn(message = ""): void {
  $("app").hidden = true;
  $("auth-screen").hidden = false;
  $("auth-error").textContent = message;

  const onSignedIn = (user: ApiUser) => {
    $("auth-screen").hidden = true;
    void startApp(user);
  };
  const onError = (e: unknown) => {
    $("auth-error").textContent = e instanceof Error ? e.message : "Sign-in failed";
  };

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  if (clientId) {
    renderGoogleButton($("google-signin"), clientId, (credential) =>
      auth.signInWithGoogle(credential).then(onSignedIn, onError),
    ).catch(() => {
      $("auth-error").textContent = "Couldn't load Google sign-in. Check your connection.";
    });
  } else if (!import.meta.env.DEV) {
    $("auth-error").textContent = "Sign-in isn't configured (missing Google client ID).";
  }

  if (import.meta.env.DEV) {
    const form = $<HTMLFormElement>("dev-signin");
    form.hidden = false;
    form.onsubmit = (e) => {
      e.preventDefault();
      auth.devSignIn($<HTMLInputElement>("dev-email").value.trim()).then(onSignedIn, onError);
    };
  }
}

// ---------- app start ----------

async function startApp(user: ApiUser): Promise<void> {
  currentUser = user;
  $("app").hidden = false;
  store = new SyncStore(user.id, auth);
  window.WrepsStore = store;
  store.onStatus(paintSyncStatus);
  await window.WrepsApp.start();
  store.onRemoteChange((data, reason) => {
    window.WrepsApp.applyRemote(data);
    if (reason === "conflict") window.showToast("Updated from another device");
  });

  // Keep devices in step: sync when the app comes back into view or online, and
  // every 30s while it's open.
  const syncIfVisible = () => {
    if (document.visibilityState === "visible") void store?.syncNow();
  };
  document.addEventListener("visibilitychange", syncIfVisible);
  window.addEventListener("online", syncIfVisible);
  setInterval(syncIfVisible, 30_000);
}

auth.onChange((user) => {
  // Session ended elsewhere (signed out on the server, account deleted, token reuse).
  if (!user && currentUser) {
    window.showToast("You've been signed out");
    setTimeout(() => location.reload(), 1500);
  }
});

async function boot(): Promise<void> {
  const result = await auth.refresh();
  if (result === "ok" && auth.user) return startApp(auth.user);
  if (result === "offline") {
    const last = Auth.lastUser();
    if (last) return startApp(last); // work offline; sync resumes when back online
    return showSignIn("You're offline. Connect to sign in.");
  }
  showSignIn();
}

void boot();

// ---------- app updates (service worker) ----------

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  import("virtual:pwa-register").then(({ registerSW }) => {
    const update = registerSW({
      onNeedRefresh() {
        const bar = document.createElement("div");
        bar.className = "update-banner";
        bar.innerHTML = "<span>A new version of Wreps is ready.</span>";
        const btn = document.createElement("button");
        btn.textContent = "Update";
        btn.onclick = () => void update(true);
        bar.append(btn);
        document.body.append(bar);
      },
    });
  });
}
